import { Dispatch, SetStateAction, useCallback } from "react";
import { useEffect, useRef, useState } from "react";
import { getUsers } from "../../services/userService";
import { GetUsersResponse, UserSearchResponse } from "../../proto/api_pb";
import { User } from "@auth0/auth0-react";
import { getThumbnailUrl } from "../../utils/thumbnailUtil";
import { Box, CircularProgress } from "@mui/material";
import { ScrollContainer, exploreTracksStyles } from "./styles";
import { ExploreArtistsCard } from "./ExploreArtistCard";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { Inbox, Session } from "talkjs/all";

export const ExploreArtists: React.FC<{
  genres: string[];
  session: Session | undefined;
  setInbox: Dispatch<SetStateAction<Inbox | undefined>>;
}> = ({ genres, session, setInbox }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isFeaturedArtistLoaded, setIsFeaturedArtistLoaded] =
    useState<boolean>(false);
  const [featuredUserSearch, setFeaturedUserSearch] = useState<
    UserSearchResponse[]
  >([]);
  const [regularArtists, setRegularArtists] = useState<UserSearchResponse[]>(
    []
  );
  const [isApiCallAvailable, setIsApiCallAvailable] = useState<boolean>(true);
  const loadingRef = useRef<HTMLDivElement | null>(null);
  const {
    page,
    scrollContainerRef,
    setIsScrollEnabled,
    isSearchScrollEnabled,
    searchPage,
    setPage,
  } = useInfiniteScroll();

  const mapUserToUserSearchResponse = async (user: User) => {
    const u = new UserSearchResponse();
    u.setUsername(user.getUsername());
    u.setUserId(user.getUserId());
    u.setSkillsList(user.getSkillsList());
    u.setGenresList(user.getGenresList());
    const imageUrl = user.getProfilePicture();
    const thumbnailUrl = imageUrl ? await getThumbnailUrl(imageUrl) : "";
    u.setProfilePicture(thumbnailUrl);
    return u;
  };
  useEffect(() => {
    setPage(0);
    setIsApiCallAvailable(true);
    setRegularArtists([]);
    setFeaturedUserSearch([]);
    getFeaturedArtists();
    getRegularArtists();
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genres]);

  const getFeaturedArtists = useCallback(async () => {
    try {
      const response = await getUsers(true, 10, 0, genres, "");
      if (response instanceof GetUsersResponse) {
        const featuredUsers = await Promise.all(
          response.getUsersList().map(mapUserToUserSearchResponse)
        );
        setFeaturedUserSearch(featuredUsers);
        setIsFeaturedArtistLoaded(true);
      }
    } catch (er) {
      console.log(er);
    }
  }, [genres]);

  const getRegularArtists = useCallback(async () => {
    if (!isApiCallAvailable) return;
    try {
      const usersLimit = 8;
      const response = await getUsers(
        false,
        usersLimit,
        isSearchScrollEnabled ? searchPage : page,
        genres,
        ""
      );
      if (response instanceof GetUsersResponse) {
        const users = await Promise.all(
          response.getUsersList().map(mapUserToUserSearchResponse)
        );
        if (users.length === 0) {
          setIsApiCallAvailable(false);
          setLoading(false);
        }

        setRegularArtists((prevUsers) => {
          const filteredUsers = users.filter(
            (user) =>
              !prevUsers.some(
                (prevUser) => prevUser.getUserId() === user.getUserId()
              )
          );
          return [...prevUsers, ...filteredUsers];
        });
        setIsScrollEnabled(true);
      }
    } catch (er) {
      console.log(er);
    }
  }, [
    genres,
    isApiCallAvailable,
    isSearchScrollEnabled,
    page,
    searchPage,
    setIsScrollEnabled,
  ]);

  useEffect(() => {
    if (!isFeaturedArtistLoaded) getFeaturedArtists();
    getRegularArtists();
  }, [getFeaturedArtists, getRegularArtists, isFeaturedArtistLoaded]);

  return (
    <>
      <ScrollContainer ref={scrollContainerRef}>
        <ExploreArtistsCard
          artists={featuredUserSearch}
          title="Featured Artists"
          session={session}
          setInbox={setInbox}
        />
        {isFeaturedArtistLoaded && regularArtists.length > 0 && (
          <ExploreArtistsCard
            artists={regularArtists}
            title="Artists"
            session={session}
            setInbox={setInbox}
          />
        )}
        {loading && (
          <Box ref={loadingRef} sx={exploreTracksStyles.loadingContainer}>
            <CircularProgress />
          </Box>
        )}
      </ScrollContainer>
    </>
  );
};
